import { supabase } from '@/integrations/supabase/client';
import { getLogger } from '@/utils/logger';

const log = getLogger({ module: 'MatureContentService' });

export interface MatureContentSettings {
  ageVerified: boolean;
  matureContentEnabled: boolean;
}

class MatureContentService {
  private static readonly AGE_VERIFIED_KEY = 'age_verified';
  private static readonly MATURE_CONTENT_KEY = 'allow_mature_content';

  /**
   * Get mature content settings from localStorage
   */
  static getLocalSettings(): MatureContentSettings {
    const ageVerified = localStorage.getItem(this.AGE_VERIFIED_KEY) === 'true';
    const matureContentEnabled = localStorage.getItem(this.MATURE_CONTENT_KEY) === 'true';
    
    return {
      ageVerified,
      matureContentEnabled
    };
  }

  /**
   * Set mature content settings in localStorage
   */
  static setLocalSettings(settings: MatureContentSettings): void {
    localStorage.setItem(this.AGE_VERIFIED_KEY, settings.ageVerified ? 'true' : 'false');
    localStorage.setItem(this.MATURE_CONTENT_KEY, settings.matureContentEnabled ? 'true' : 'false');
    
    log.info('Updated local mature content settings', settings);
  }

  /**
   * Load mature content settings from database and sync to localStorage
   */
  static async loadAndSyncSettings(userId: string): Promise<MatureContentSettings> {
    try {
      log.info('Loading mature content settings from database', { userId });
      
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('age_verified_at, mature_content_enabled')
        .eq('id', userId)
        .single();

      if (error) {
        log.error('Error loading mature content settings from database', error);
        // Fall back to localStorage if database fails
        return this.getLocalSettings();
      }

      const settings: MatureContentSettings = {
        ageVerified: !!profile?.age_verified_at,
        matureContentEnabled: !!profile?.mature_content_enabled
      };

      // Sync to localStorage
      this.setLocalSettings(settings);
      
      log.info('Successfully loaded and synced mature content settings', settings);
      return settings;
    } catch (error) {
      log.error('Error in loadAndSyncSettings', error);
      // Fall back to localStorage if there's an error
      return this.getLocalSettings();
    }
  }

  /**
   * Update mature content settings both in database and localStorage
   */
  static async updateSettings(
    userId: string, 
    settings: MatureContentSettings
  ): Promise<void> {
    try {
      log.info('Updating mature content settings', { userId, settings });
      
      // Update localStorage first for immediate UI response
      this.setLocalSettings(settings);

      // Update database
      const updates = {
        mature_content_enabled: settings.matureContentEnabled,
        age_verified_at: settings.ageVerified ? new Date().toISOString() : null
      };

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId);

      if (error) {
        log.error('Error updating mature content settings in database', error);
        throw error;
      }

      log.info('Successfully updated mature content settings in database');
    } catch (error) {
      log.error('Error in updateSettings', error);
      throw error;
    }
  }

  /**
   * Check if mature content should be shown
   */
  static shouldShowMatureContent(): boolean {
    const settings = this.getLocalSettings();
    return settings.ageVerified && settings.matureContentEnabled;
  }

  /**
   * Protect settings from being cleared during auth state changes
   */
  static protectSettings(): void {
    // Store current settings in a protected key
    const settings = this.getLocalSettings();
    localStorage.setItem('_protected_mature_settings', JSON.stringify(settings));
  }

  /**
   * Restore settings from protected storage
   */
  static restoreProtectedSettings(): void {
    try {
      const protectedSettings = localStorage.getItem('_protected_mature_settings');
      if (protectedSettings) {
        const settings = JSON.parse(protectedSettings);
        this.setLocalSettings(settings);
        localStorage.removeItem('_protected_mature_settings');
        log.info('Restored protected mature content settings', settings);
      }
    } catch (error) {
      log.error('Error restoring protected settings', error);
    }
  }

  /**
   * Clear all mature content settings
   */
  static clearSettings(): void {
    localStorage.removeItem(this.AGE_VERIFIED_KEY);
    localStorage.removeItem(this.MATURE_CONTENT_KEY);
    localStorage.removeItem('_protected_mature_settings');
    log.info('Cleared all mature content settings');
  }
}

export default MatureContentService;